import { useCallback, useState } from 'react';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import { compose } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import { hasPermissionByIds } from '@gef-ui/cdu-user/selectors';
import { getLabelMessage } from '@gef-ui/localization/messages';
import { useLocalConfirmDialog } from '@gef-ui/material/components/Dialog/ConfirmDialog';

import { codelistProvider } from 'components';
import Panel from 'components/atoms/GefMui/Panel';
import Subheader from 'components/atoms/GefMui/Subheader';
import { codelists } from 'constants/codelists';
import PAGES from 'constants/pages';
import { KCL_AREA_SETTINGS } from 'features/common/acl/constants';
import { useRenderSubheaderTitle } from 'features/common/Subheader/hooks';
import { changeTab } from 'features/views/settings/actions';
import { TABS } from 'features/views/settings/constants';
import { getAgencyManagersAndSubordinates } from 'features/views/settings/marketPlaceAgencies/selectors';
import { reset as resetMarketPlaceAgenciesTab } from 'features/views/settings/marketPlaceAgencies/slice';
import { getActiveTab } from 'features/views/settings/selectors';
import { getContextUser } from 'features/views/userContext/selectors';

import InformationCreationTab from './components/InformationCreation/InformationCreationTab';
import MarketPlaceAgenciesTab from './components/MarketPlaceAgencies/MarketPlaceAgenciesTab';
import NotificationSendingTab from './components/NotificationSending/NotificationSendingTab';

export const Settings = () => {
	const dispatch = useDispatch();
	const contextUser = useSelector(getContextUser);
	const currentTabValue = useSelector(getActiveTab);
	const marketPlaceAgencyManagersAndSubordinates = useSelector(getAgencyManagersAndSubordinates);
	const [tabToRedirectBeforeConfirm, setTabToRedirectBeforeConfirm] = useState(null);

	const [confirmLeaveBeforeSaving, openConfirmLeaveBeforeSaving] = useLocalConfirmDialog({
		backdropClose: false,
		title: getLabelMessage('confirm:doYouWantToLeave'),
		content: getLabelMessage('settings:marketPlaceAgencies.confirmChangeFilterOption'),
		onOk: () => {
			dispatch(changeTab(tabToRedirectBeforeConfirm));
			setTabToRedirectBeforeConfirm(null);
			dispatch(resetMarketPlaceAgenciesTab());
		},
	});

	const generateScreenInfo = useCallback(() => {
		const result = {};
		switch (currentTabValue) {
			case TABS.notificationSending:
				result.screenId = PAGES.settings.notificationSending.id;
				result.screenName = PAGES.settings.notificationSending.name;
				break;
			case TABS.informationCreation:
				result.screenId = PAGES.settings.informationCreation.id;
				result.screenName = PAGES.settings.informationCreation.name;
				break;
			case TABS.marketPlaceAgencies:
				result.screenId = PAGES.settings.marketPlaceAgencies.id;
				result.screenName = PAGES.settings.marketPlaceAgencies.name;
				break;
		}
		return result;
	}, [currentTabValue]);

	const { screenId, screenName } = generateScreenInfo();

	const handleTabChange = useCallback(
		(e, tab) => {
			if (
				currentTabValue === TABS.marketPlaceAgencies &&
				!isNilOrEmpty(marketPlaceAgencyManagersAndSubordinates)
			) {
				setTabToRedirectBeforeConfirm(tab);
				openConfirmLeaveBeforeSaving();
			} else {
				dispatch(changeTab(tab));
			}
		},
		[dispatch, currentTabValue, marketPlaceAgencyManagersAndSubordinates, openConfirmLeaveBeforeSaving],
	);

	const subheaderTitle = useRenderSubheaderTitle({
		contextUser,
		pageTitle: getLabelMessage('settings:mainHeader'),
	});

	const hasMarketPlaceAgenciesTabPerm = useSelector((state) => hasPermissionByIds(state)(KCL_AREA_SETTINGS));

	const showMarketPlaceAgenciesTab = hasMarketPlaceAgenciesTabPerm && isNilOrEmpty(contextUser);

	return (
		<TabContext value={currentTabValue}>
			{confirmLeaveBeforeSaving}
			<Panel screenId={screenId} screenName={screenName}>
				<Paper sx={{ p: 3, borderRadius: 2 }}>
					<Stack spacing={2}>
						<Subheader title={subheaderTitle} />
						<TabList onChange={handleTabChange}>
							<Tab
								label={getLabelMessage('settings:notificationSending')}
								value={TABS.notificationSending}
							/>
							<Tab
								label={getLabelMessage('settings:informationCreation')}
								value={TABS.informationCreation}
							/>
							{showMarketPlaceAgenciesTab && (
								<Tab
									label={getLabelMessage('settings:marketPlaceAgencies.header')}
									value={TABS.marketPlaceAgencies}
								/>
							)}
						</TabList>
					</Stack>
				</Paper>
				<TabPanel value={TABS.notificationSending} sx={{ p: 0 }}>
					<NotificationSendingTab />
				</TabPanel>
				<TabPanel value={TABS.informationCreation} sx={{ p: 0 }}>
					<InformationCreationTab />
				</TabPanel>
				<TabPanel value={TABS.marketPlaceAgencies} sx={{ p: 0 }}>
					<MarketPlaceAgenciesTab />
				</TabPanel>
			</Panel>
		</TabContext>
	);
};

export default compose(
	codelistProvider(codelists.taskTypes, 'taskTypes'),
	codelistProvider(codelists.notificationsSet, 'notificationsSet'),
	codelistProvider(codelists.notificationsSetContacts, 'notificationsSetContacts'),
)(Settings);
